import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';

import { SectionLabel } from './SectionLabel';
import { PartTypeLiveSearchItem } from '../../../../@types/partType.type';
import { useGetPartTypesByClientContract } from '../../../../queries/part-type-query';

export interface ClientPartTypeFieldProps {
  clientId: number;
  contractId: number;
  partTypeId?: number;
  partTypeName: string;
  onChange: (partType: PartTypeLiveSearchItem | null) => void;
  locked?: boolean;
  lockReason?: string;
}

export const ClientPartTypeField: React.FC<ClientPartTypeFieldProps> = ({
  clientId,
  contractId,
  partTypeId,
  partTypeName,
  onChange,
  locked,
  lockReason,
}) => {
  const partTypesQuery = useGetPartTypesByClientContract(clientId, contractId);
  const data = partTypesQuery.data;

  // The query has `enabled: false`; refetch whenever the client/contract pair changes.
  useEffect(() => {
    if (clientId > 0) partTypesQuery.refetch();
  }, [clientId, contractId]);

  const options = useMemo(() => data ?? [], [data]);

  const selected = useMemo(
    () =>
      partTypeId
        ? (options.find((opt) => opt.id === partTypeId) ?? null)
        : partTypeName
          ? (options.find((opt) => opt.name === partTypeName) ?? null)
          : null,
    [options, partTypeId, partTypeName]
  );

  // After options refetch, drop a previously-selected partTypeId that's no
  // longer in the list (e.g., user changed location → different client/contract).
  // Skip while data is still loading to avoid clobbering a valid selection.
  // `onChange` is intentionally not a dep — parents pass inline callbacks, so
  // including it would re-run on every render; we read the latest closure when
  // data/partTypeId actually change.
  useEffect(() => {
    if (!data || !partTypeId) return;
    if (!data.some((opt) => opt.id === partTypeId)) onChange(null);
  }, [data, partTypeId]);

  const disabled = !!locked || !clientId;

  return (
    <SectionLabel label="Part Type" locked={locked} lockReason={lockReason}>
      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        value={selected}
        onChange={(_, val) => onChange(val)}
        disabled={disabled}
        loading={partTypesQuery.isFetching}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select a part type"
            slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': 'Part Type' } }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{option.name}</Typography>
              {option.number && (
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  {option.number}
                </Typography>
              )}
            </Box>
          </li>
        )}
      />
    </SectionLabel>
  );
};
